import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    ShieldCheck,
    Mail,
    Smartphone,
    CheckCircle2,
    Loader2,
    Copy,
    Download,
} from "lucide-react";
import { toast } from "sonner";

/**
 * In-Settings TOTP enrolment wizard for the whitelisted accounts
 * (المنصوري / الحميري / الراقية للذهب). Login never challenges these
 * accounts; this section lets them enable TOTP for sensitive actions /
 * recovery, gated by their account email.
 *
 * Flow:
 *   1. User enters the email tied to the account → /auth/totp/settings-setup
 *   2. QR + manual secret displayed → user scans → enters 6-digit code →
 *      /auth/totp/setup/verify
 *   3. 6 backup codes shown ONCE (copy / download / acknowledge → done)
 */
export default function TotpEnrolSection() {
    const { t } = useLang();
    const { refresh } = useAuth();

    const [step, setStep] = useState(1);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    // Step 1
    const [email, setEmail] = useState("");

    // After step 1 we carry these forward
    const [setupToken, setSetupToken] = useState("");
    const [qrUrl, setQrUrl] = useState("");
    const [secret, setSecret] = useState("");

    // Step 2
    const [code, setCode] = useState("");

    // Step 3
    const [backupCodes, setBackupCodes] = useState([]);
    const [savedAck, setSavedAck] = useState(false);

    const submitEmail = async (e) => {
        e?.preventDefault?.();
        setError("");
        if (!email.trim()) { setError(t.emailRequired); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/totp/settings-setup", { email: email.trim() });
            setSetupToken(data.setup_token);
            setSecret(data.secret);
            setQrUrl(data.qr_data_url);
            setStep(2);
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const verifyCode = async (e) => {
        e?.preventDefault?.();
        setError("");
        const clean = (code || "").replace(/\D/g, "");
        if (clean.length !== 6) { setError(t.totpInvalidFormat); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/totp/setup/verify", {
                setup_token: setupToken, code: clean,
            });
            setBackupCodes(data.backup_codes || []);
            toast.success(t.totpEnabledToast);
            setStep(3);
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const copySecret = async () => {
        try { await navigator.clipboard.writeText(secret); toast.success(t.copiedToast); }
        catch { /* clipboard unavailable */ }
    };

    const copyAllCodes = async () => {
        try { await navigator.clipboard.writeText(backupCodes.join("\n")); toast.success(t.copiedToast); }
        catch { /* clipboard unavailable */ }
    };

    const downloadCodes = () => {
        const blob = new Blob(
            [`${t.brand} — ${t.totpBackupCodesTitle}\n\n${backupCodes.join("\n")}\n\n${t.totpBackupCodesFootnote}`],
            { type: "text/plain;charset=utf-8" },
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "meyar-backup-codes.txt";
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const finishAndRefresh = async () => {
        await refresh();
        setStep(1);
        setEmail("");
        setCode("");
        setBackupCodes([]);
        setSavedAck(false);
    };

    return (
        <section
            className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4 shadow-sm sm:p-6"
            data-testid="settings-totp-section"
        >
            <div className="mb-3 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-700" />
                <h2 className="font-heading text-base font-bold text-emerald-900 sm:text-lg">{t.settingsTotpTitle}</h2>
            </div>
            <p className="text-xs text-emerald-900/80 sm:text-sm">{t.settingsTotpSubtitle}</p>

            <ol className="mt-4 flex items-center gap-2 text-xs">
                {[1, 2, 3].map((n) => (
                    <li key={n} className="flex flex-1 items-center gap-2">
                        <span
                            className={`flex h-6 w-6 items-center justify-center rounded-full font-bold ${
                                step > n
                                    ? "bg-emerald-700 text-white"
                                    : step === n
                                        ? "bg-emerald-600 text-white"
                                        : "bg-neutral-200 text-neutral-500"
                            }`}
                        >
                            {step > n ? <CheckCircle2 className="h-3.5 w-3.5" /> : n}
                        </span>
                        <span className={`flex-1 text-[11px] ${step === n ? "font-bold text-neutral-800" : "text-neutral-500"}`}>
                            {n === 1 ? t.settingsTotpStep1 : n === 2 ? t.settingsTotpStep2 : t.settingsTotpStep3}
                        </span>
                    </li>
                ))}
            </ol>

            {step === 1 ? (
                <form onSubmit={submitEmail} className="mt-4 space-y-3" data-testid="totp-settings-step1-form">
                    <p className="text-xs text-neutral-700">{t.settingsTotpStep1Hint}</p>
                    <div>
                        <Label className="mb-1.5 block text-sm font-medium">{t.emailLabel}</Label>
                        <div className="relative">
                            <Mail className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="h-11 ltr-text pe-10 ps-3"
                                dir="ltr"
                                placeholder="you@example.com"
                                required
                                data-testid="totp-settings-email-input"
                            />
                        </div>
                    </div>
                    {error ? (
                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="totp-settings-error">
                            {error}
                        </div>
                    ) : null}
                    <Button
                        type="submit"
                        disabled={busy}
                        className="h-11 w-full bg-emerald-700 text-white hover:bg-emerald-800"
                        data-testid="totp-settings-step1-submit"
                    >
                        {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}
                        {t.settingsTotpVerifyEmailBtn}
                    </Button>
                </form>
            ) : step === 2 ? (
                <form onSubmit={verifyCode} className="mt-4 space-y-3" data-testid="totp-settings-step2-form">
                    <p className="text-xs text-neutral-700">{t.settingsTotpStep2Hint}</p>
                    <div className="flex justify-center">
                        <img src={qrUrl} alt="QR" className="h-44 w-44 rounded-lg border border-neutral-200 bg-white" data-testid="totp-settings-qr-img" />
                    </div>
                    <div>
                        <Label className="text-xs text-neutral-500">{t.totpSecretLabel}</Label>
                        <div className="mt-1 flex items-center gap-2 rounded-md bg-white px-2 py-1.5 ring-1 ring-neutral-200">
                            <code className="flex-1 break-all font-numbers text-xs text-neutral-700" data-testid="totp-settings-secret">{secret}</code>
                            <button type="button" onClick={copySecret} className="rounded p-1 hover:bg-neutral-100" data-testid="totp-settings-copy-secret-btn">
                                <Copy className="h-3.5 w-3.5 text-neutral-600" />
                            </button>
                        </div>
                    </div>
                    <div>
                        <Label className="mb-1.5 block text-sm font-medium">{t.totpCodeLabel}</Label>
                        <div className="relative">
                            <Smartphone className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                            <Input
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                className="h-11 pe-10 ps-3 font-numbers tracking-[0.4em] text-center text-lg"
                                placeholder="••••••"
                                inputMode="numeric"
                                autoFocus
                                data-testid="totp-settings-code-input"
                            />
                        </div>
                    </div>
                    {error ? (
                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="totp-settings-error">
                            {error}
                        </div>
                    ) : null}
                    <Button
                        type="submit"
                        disabled={busy}
                        className="h-11 w-full bg-emerald-700 text-white hover:bg-emerald-800"
                        data-testid="totp-settings-step2-submit"
                    >
                        {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}
                        {t.totpActivateBtn}
                    </Button>
                </form>
            ) : (
                <div className="mt-4 space-y-3" data-testid="totp-settings-step3">
                    <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                        <CheckCircle2 className="h-4 w-4" />
                        <span>{t.totpEnabledBanner}</span>
                    </div>
                    <h3 className="font-heading text-sm font-bold">{t.settingsTotpBackupTitle}</h3>
                    <p className="text-xs text-neutral-600">{t.totpBackupCodesSubtitle}</p>
                    <ul className="grid grid-cols-2 gap-2 font-numbers text-sm" data-testid="totp-settings-backup-codes-list">
                        {backupCodes.map((c, i) => (
                            <li
                                key={c}
                                className="rounded-md bg-white px-2 py-2 text-center font-bold text-neutral-800 ring-1 ring-amber-200"
                                data-testid={`totp-settings-backup-code-${i}`}
                            >
                                {c}
                            </li>
                        ))}
                    </ul>
                    <div className="flex gap-2">
                        <Button type="button" variant="outline" onClick={copyAllCodes} className="flex-1" data-testid="totp-settings-copy-all-btn">
                            <Copy className="me-1 h-4 w-4" />
                            {t.totpCopyAll}
                        </Button>
                        <Button type="button" variant="outline" onClick={downloadCodes} className="flex-1" data-testid="totp-settings-download-btn">
                            <Download className="me-1 h-4 w-4" />
                            {t.totpDownloadTxt}
                        </Button>
                    </div>
                    <label className="flex items-start gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm">
                        <input
                            type="checkbox"
                            checked={savedAck}
                            onChange={(e) => setSavedAck(e.target.checked)}
                            className="mt-0.5"
                            data-testid="totp-settings-ack-checkbox"
                        />
                        <span>{t.totpAckSaved}</span>
                    </label>
                    <Button
                        type="button"
                        disabled={!savedAck}
                        onClick={finishAndRefresh}
                        className="h-11 w-full bg-neutral-900 text-white hover:bg-neutral-800"
                        data-testid="totp-settings-done-btn"
                    >
                        {t.totpFinishSetup}
                    </Button>
                </div>
            )}
        </section>
    );
}
